import * as Icons from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { FEATURES, STEPS, HIGHLIGHTS, IMAGES } from "@/lib/content";

const Icon = ({ name, className }) => {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} />;
};

export const Highlights = () => {
  return (
    <section className="bg-white pt-[35px] lg:pt-[50px] pb-[35px] lg:pb-[50px]" data-testid="highlights-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
          {HIGHLIGHTS.map((h, i) => (
            <Reveal key={h.title} delay={i * 0.08} className="h-full">
              <a
                href={h.href}
                data-testid={`highlight-${i}`}
                className="group flex h-full items-stretch overflow-hidden rounded-[18px] bg-white border border-[#E5E5E5]"
              >
                <span
                  className="flex w-16 shrink-0 items-center justify-center bg-[#F8B808]"
                >
                  <Icon name={h.icon} className="h-6 w-6 text-[#1A1A1A]" strokeWidth={2} />
                </span>
                <span className="flex flex-col justify-center px-4 py-4">
                  <span className="font-heading text-base font-bold text-[#1A1A1A] leading-tight">
                    {h.title}
                  </span>
                  <span className="font-body text-sm text-[#6B6B66] mt-1 leading-snug">
                    {h.text}
                  </span>
                </span>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Overview = () => {
  return (
    <section id="overview" className="py-[35px] lg:py-[50px] bg-white" data-testid="overview-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-[40.5px] lg:gap-24 items-center">
          <Reveal className="order-2 lg:order-1">
            <div className="overflow-hidden rounded-[8px] aspect-[4/3]">
              <img
                src="https://customer-assets.emergentagent.com/job_discount-retail-hub-1/artifacts/8cklipx1_f6f09a2b-bd62-4276-9d3d-79ea2ad4e180.png"
                alt="Home Cart warehouse aisle stocked with brand-name appliances and household products"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>

          <div className="order-1 lg:order-2">
            <h2 className="font-heading text-[31px] sm:text-5xl lg:text-[50px] font-medium tracking-tight text-[#1A1A1A] leading-[1.04]">
              The Home Cart story
            </h2>
            <Reveal delay={0.1}>
              <p className="font-body text-base md:text-lg text-[#6B6B66] mt-6 lg:mt-[41px] leading-relaxed">
                Home Cart brings exceptional value to everyday shoppers by
                sourcing Costco overstock, stock overruns and customer returns.
                We offer trusted household products, electronics and home
                essentials at significantly reduced prices.
              </p>
              <p className="font-body text-base md:text-lg text-[#6B6B66] mt-6 leading-relaxed">
                Every product is carefully inspected, sorted and prepared for
                resale, allowing us to offer recognizable brands at a fraction
                of traditional retail prices.
              </p>
              <p className="font-body text-base md:text-lg text-[#6B6B66] mt-6 leading-relaxed">
                The result is quality products, exceptional savings and a
                constantly changing selection that makes every visit worth
                exploring.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export const About = () => {
  return (
    <section id="about" className="py-[35px] lg:py-[50px] bg-[#FFFFFF]" data-testid="about-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-[30.7px] lg:gap-24 items-center">
          <div className="order-1">
            <SectionHeading
              align="left"
              eyebrow="The Home Cart Difference"
              title="Why Home Cart"
            />
            <Reveal delay={0.1}>
              <p className="font-body text-lg text-[#6B6B66] mt-[19px] lg:mt-[41px] leading-relaxed max-w-lg lg:max-w-xl">
                Home Cart gives shoppers access to trusted brands,
                <br className="hidden lg:block" /> constantly changing inventory
                and significant savings
                <br className="hidden lg:block" /> on everyday household products.
              </p>
            </Reveal>

            <div className="mt-[27px] lg:mt-8">
              {FEATURES.map((f, i) => (
                <Reveal key={f.title} delay={0.15 + i * 0.08}>
                  <div
                    data-testid={`about-feature-${i}`}
                    className="flex items-start gap-4 py-[13.5px] lg:py-[15px] group"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white border border-[#E5E5E5] transition-colors group-hover:border-[#F8B808]">
                      <Icon name={f.icon} className="h-5 w-5 text-[#1A1A1A]" />
                    </span>
                    <div>
                      <h3 className="font-heading text-xl font-semibold text-[#1A1A1A]">
                        {f.title}
                      </h3>
                      <p className="font-body text-base text-[#6B6B66] mt-1.5 leading-relaxed">
                        {f.text}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal className="order-2">
            <div className="overflow-hidden rounded-[8px] aspect-[40/33]">
              <img
                src="https://customer-assets.emergentagent.com/job_discount-retail-hub-1/artifacts/sfk2gbq3_c303ae71-5e45-4e0d-936f-afd13b869d7f.png"
                alt="A shopper browsing brand-name appliances in a warehouse retail aisle"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export const HowItWorks = () => {
  return (
    <section id="how-it-works" className="py-[35px] lg:py-[50px] bg-white" data-testid="how-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          eyebrow="The Process"
          title="How Home Cart works"
        />
        <div className="mt-6 lg:mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.1}>
              <div data-testid={`step-${i}`} className="group">
                <span className="block font-heading text-5xl font-bold text-[#F8B808] tabular-nums leading-none">
                  0{i + 1}
                </span>
                <h3 className="font-heading text-xl font-semibold text-[#1A1A1A] mt-4">
                  {s.title}
                </h3>
                <p className="font-body text-base text-[#6B6B66] mt-2 leading-relaxed">
                  {s.text}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
